import { omit } from "lodash";

import { formatPhysician } from "utils/physicians.utils";

import { formatValidQueryParamUrlCommaSeparated } from "../utils/common";
import { emptySplitApi } from "./empty-split-api";
import {
  CheckNPIResponse,
  IntakeReviewPatients,
  IntakeReviewResolution,
  NPPESSearchParams,
  NPPESSearchResponse,
  Physician,
  PhysicianCSVQuery,
  PhysicianCommunicationDetail,
  PhysicianCommunicationFilter,
  PhysicianCommunicationsFilter,
  PhysicianCommunicationsResponse,
  PhysicianCountFilter,
  PhysicianFilter,
  PhysicianMergeDetails,
  PhysiciansResponse
} from "./types/physician.type";
import { CSVReportResponse } from "./types/reports.type";

const physiciansApi = emptySplitApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getPhysicians: builder.query<PhysiciansResponse, PhysicianFilter>({
      query: (filter) =>
        `referring-physicians/?is_active=${filter?.is_active ?? ""}${
          filter?.search ? `&search=${encodeURIComponent(filter.search)}` : ""
        }${filter?.patient ? `&patient=${filter.patient}` : ""}${
          filter?.intake_review_pending !== undefined
            ? `&intake_review_pending=${filter.intake_review_pending}`
            : ""
        }${
          filter?.query ? `&query=${encodeURIComponent(filter.query)}` : ""
        }&limit=${
          filter?.limit || 300
        }&offset=${filter?.offset || 0}&ordering=last_name,first_name,pk`,
      providesTags: ["Physicians"],
      transformResponse: (response: PhysiciansResponse) =>
        response.map((referringPhysician) =>
          formatPhysician(referringPhysician)
        )
    }),
    getPhysiciansCount: builder.query<number, PhysicianCountFilter>({
      query: ({ is_active, intake_review_pending }) =>
        `referring-physicians/count/?is_active=${is_active}${
          intake_review_pending !== undefined
            ? `&intake_review_pending=${intake_review_pending}`
            : ""
        }`,
      providesTags: ["Physicians"]
    }),
    getIntakeReviewPatients: builder.query<IntakeReviewPatients, number>({
      query: (physicianId) =>
        `referring-physicians/${physicianId}/intake-review-patients/`,
      providesTags: ["Physicians"]
    }),
    resolveIntakePhysicianReview: builder.mutation<
      Physician,
      IntakeReviewResolution
    >({
      query: ({ physicianId, action, targetId }) => ({
        url: `referring-physicians/${physicianId}/resolve-intake-review/`,
        method: "PATCH",
        body: { action, ...(targetId ? { target_id: targetId } : {}) }
      }),
      // Link and dismiss repoint or clear the physician on patient charts, so an open chart must refetch.
      invalidatesTags: ["Physicians", "PhysicianCommunications", "Patients"]
    }),
    getPhysician: builder.query<Physician, number>({
      query: (referringPhysicianId) =>
        `referring-physicians/${referringPhysicianId}/`,
      providesTags: ["Physicians"],
      transformResponse: (referringPhysician: Physician) =>
        formatPhysician(referringPhysician)
    }),
    addPhysician: builder.mutation<Physician, Physician>({
      query: (referringPhysician) => ({
        url: "referring-physicians/",
        method: "POST",
        body: referringPhysician
      }),
      invalidatesTags: ["Physicians"]
    }),
    editPhysician: builder.mutation<Physician, Physician>({
      query: (referringPhysician) => ({
        url: `referring-physicians/${referringPhysician.id}/`,
        method: "PATCH",
        body: omit(referringPhysician, "id")
      }),
      invalidatesTags: ["Physicians"]
    }),
    downloadPhysiciansCSV: builder.query<CSVReportResponse, PhysicianCSVQuery>({
      query: (filters) => ({
        url: `referring-physicians/?${formatValidQueryParamUrlCommaSeparated(
          filters
        )}`,
        headers: { Accept: "custom-csv/json" }
      })
    }),
    npiSearch: builder.query<NPPESSearchResponse, NPPESSearchParams>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params.number) searchParams.set("number", params.number);
        if (params.first_name)
          searchParams.set("first_name", params.first_name);
        if (params.last_name) searchParams.set("last_name", params.last_name);
        if (params.city) searchParams.set("city", params.city);
        if (params.state) searchParams.set("state", params.state);
        if (params.limit) searchParams.set("limit", String(params.limit));
        if (params.skip) searchParams.set("skip", String(params.skip));
        return `referring-physicians/npi-search/?${searchParams.toString()}`;
      }
    }),
    checkNpi: builder.query<CheckNPIResponse, string>({
      query: (npi) => `referring-physicians/check-npi/${npi}/`
    }),
    getMergePhysicianDetails: builder.query<
      [PhysicianMergeDetails, PhysicianMergeDetails],
      { source_id: number; dest_id: number }
    >({
      query: (params) => ({
        url: "referring-physicians/merge-details/",
        params
      })
    }),
    mergePhysicians: builder.mutation<
      Physician,
      { source_id: number; dest_id: number }
    >({
      query: (body) => ({
        url: "referring-physicians/merge/",
        method: "PATCH",
        body
      }),
      // A merge repoints the source physician's communication rows onto the survivor, and merge
      // lives on the same page as the history panel, so an open panel would otherwise stay stale.
      invalidatesTags: ["Physicians", "PhysicianCommunications"]
    }),
    getPhysicianCommunications: builder.query<
      PhysicianCommunicationsResponse,
      PhysicianCommunicationsFilter
    >({
      // `limit` is always sent explicitly: the server default is 5, and its max is 300 — which is why
      // the panel pages with `offset` over a fixed limit rather than widening the limit. A widening
      // window would silently clamp at 300 while `count` kept reporting the true total, so the last
      // pages would be unreachable.
      query: ({ physicianId, limit = 10, offset = 0 }) =>
        `referring-physicians/${physicianId}/communications/?limit=${limit}&offset=${offset}`,
      providesTags: ["PhysicianCommunications"]
    }),
    getPhysicianCommunication: builder.query<
      PhysicianCommunicationDetail,
      PhysicianCommunicationFilter
    >({
      query: ({ physicianId, communicationId }) =>
        `referring-physicians/${physicianId}/communications/${communicationId}/`,
      // The payload embeds pre-signed attachment URLs that expire an hour after the response was
      // built, so this must never be served from cache — the same reason `getFaxDocumentUrls` does
      // this. Dropping the entry when the last subscriber unmounts means every open of the dialog
      // mints a fresh clock. Deliberately provides no tags: nothing should be able to resurrect a
      // stale entry, and a send cannot change a message that has already gone out.
      keepUnusedDataFor: 0
    })
  })
});

export const {
  useGetPhysiciansQuery,
  useGetPhysicianQuery,
  useLazyGetPhysicianQuery,
  useAddPhysicianMutation,
  useEditPhysicianMutation,
  useLazyDownloadPhysiciansCSVQuery,
  useGetPhysiciansCountQuery,
  useLazyNpiSearchQuery,
  useLazyCheckNpiQuery,
  useGetMergePhysicianDetailsQuery,
  useMergePhysiciansMutation,
  useGetPhysicianCommunicationsQuery,
  useGetPhysicianCommunicationQuery,
  useGetIntakeReviewPatientsQuery,
  useResolveIntakePhysicianReviewMutation
} = physiciansApi;
